import React, { useEffect, useState } from 'react'
import qs from 'query-string'
import { useHistory, useLocation } from 'react-router-dom'
import { useLazyQuery, useQuery } from '@apollo/react-hooks'
import { useDispatch } from 'react-redux'

import {
  MW_ERRORS,
  MODALS,
  SIGN_UP_SCREENS,
  SUBSCRIPTION_STATUS
} from '../../../../constants'
import { getGQLErrorCode, getGQLErrorMsg, getGQLErrorDetails } from '../../../../lib/apollo'
import { setVoucherValidateError, setVoucherValidateType } from '../../../../actions'
import { usePlanDetails } from '../../../shared/hooks/use-plan-details'
import { signUp } from '../../../../lib/analytics/events'
import { useIsAuthenticated } from '../../../../hooks/use-is-authenticated'
import { useSelectPlan } from '../../hooks/use-select-plan'
import { LoadingComponent } from '../../../../components/loading'
import VOUCHER_WITH_SKU_QUERY from '../../../../../graphql/queries/voucher-with-sku.gql'
import ACCOUNT_QUERY_WITH_DIR from '../../../../../graphql/queries/account-with-directives.gql'
import PlanTableWeb from '../../../price-table/plan-table-web'
import PlanTableMobile from '../../../price-table/plan-table-mobile'
import Step from '../step'
import TempPriceAlert from './temp-price-alert'
import styles from '../sign-up.css'

/**
 * Plan selection screen for signup flow
 * Allows users to choose a subscription plan with optional voucher validation
 */
const PlanSelect = () => {
  const currentScreen = SIGN_UP_SCREENS.PLAN_SELECT
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()
  const isAuthenticated = useIsAuthenticated()

  const { voucher } = qs.parse(location.search)
  const [voucherRelatedActiveSubscription, setVoucherRelatedActiveSubscription] = useState(null)

  const { selectToNext, activeSubscription, updateActiveSubscription } = useSelectPlan()
  const { getPlanTitleBySku, getPlanSkuByType } = usePlanDetails()

  // Check if user has active subscription
  const { data: accountData, loading: isCheckingSubscription } = useQuery(ACCOUNT_QUERY_WITH_DIR, {
    variables: {
      withCreditcards: true,
      withSubscription: true
    },
    fetchPolicy: 'cache-first',
    skip: !isAuthenticated
  })

  // Validate voucher applicability
  const [
    validateVoucher,
    { data: voucherData, loading: isValidatingVoucher, error: voucherError }
  ] = useLazyQuery(VOUCHER_WITH_SKU_QUERY)

  const hasActiveSubscription = accountData?.account?.subscription?.status === SUBSCRIPTION_STATUS.ACTIVE

  // Redirect if user is not authenticated or has active subscription
  useEffect(() => {
    if (!isAuthenticated || hasActiveSubscription) {
      history.push('/')
    }

    signUp.stepViewed(currentScreen.datalayerStepSequence, currentScreen.datalayerStepLabel)
  }, [isAuthenticated, hasActiveSubscription])

  // Handle voucher validation response
  useEffect(() => {
    if (voucherError) {
      const errorCode = voucherError.graphQLErrors && getGQLErrorCode(voucherError)
      const errorDetails = voucherError.graphQLErrors && getGQLErrorDetails(voucherError)
      const errorMessage = voucherError.graphQLErrors
        ? getGQLErrorMsg(voucherError)
        : voucherError.message

      if ([MW_ERRORS.VOUCHER_NOT_APPLICABLE, MW_ERRORS.VOUCHER_INVALID].includes(errorCode)) {
        dispatch(setVoucherValidateError(errorMessage))
        dispatch(setVoucherValidateType(getPlanTitleBySku(errorDetails?.voucherSku)))

        // Show error modal
        history.replace({
          ...location,
          search: qs.stringify({
            ...qs.parse(location.search),
            plan: voucherRelatedActiveSubscription,
            modal: MODALS.qsParams.voucherValidateErrorForSignup
          })
        })
      }
      return
    }

    if (voucherData && !isValidatingVoucher) {
      // Clear voucher errors
      dispatch(setVoucherValidateError(''))
      dispatch(setVoucherValidateType(''))

      // Proceed to next step
      selectToNext(voucherRelatedActiveSubscription)
    }
  }, [
    voucherData,
    voucherError,
    isValidatingVoucher
  ])

  const handlePlanSelection = (subscription) => {
    setVoucherRelatedActiveSubscription(subscription)

    if (voucher && subscription) {
      // Validate voucher before proceeding
      validateVoucher({
        variables: {
          voucherCode: voucher,
          signupSku: getPlanSkuByType(subscription)
        }
      })
    } else {
      // No voucher - proceed directly to next step
      selectToNext(subscription)
    }
  }

  if (isCheckingSubscription) {
    return <LoadingComponent pastDelay />
  }

  return (
    <>
      <TempPriceAlert />

      <div className={styles.topContainer}>
        <Step current={currentScreen.step} total={currentScreen.totalSteps} />
        <PlanTableWeb
          selectToNext={handlePlanSelection}
          activeSubscription={activeSubscription}
          updateActiveSubscription={updateActiveSubscription}
          titleClassName={styles.header}
          inSignupJourney
        />
        <PlanTableMobile
          selectToNext={handlePlanSelection}
          activeSubscription={activeSubscription}
          updateActiveSubscription={updateActiveSubscription}
          titleClassName={styles.header}
          inSignupJourney
        />
      </div>
    </>
  )
}

export default PlanSelect
